/**
 * The PN consultation card's likely insurance eligibility (BAR-2919): one line,
 * ✓ or ✗, from the BMI, the weight-related conditions, how the patient gets
 * insurance, the insurer and its network status.
 *
 * Stricter than signup's screen, on purpose: signup says "you may be
 * eligible", the card applies the real criteria.
 *
 * | Coverage                      | Meets criteria                                     |
 * | ----------------------------- | -------------------------------------------------- |
 * | Cigna                         | BMI 35+, or 30–34.9 with a weight-related condition |
 * | Any other private insurer     | BMI 40+, or 35–39.9 with a weight-related condition |
 * | Out of network                | Not eligible yet                                   |
 * | Tricare, VA, Medicare, Medicaid | Not eligible yet                                 |
 *
 * Network status is one value per insurer. Unknown — not in bariendo-be's map,
 * the map empty, or not answered yet — keeps an eligible line eligible and
 * says "Network unsure"; it never reads as in or out of network.
 */

/** How the patient gets insurance. The card's options, in its order. */
export const INSURANCE_SOURCE_OPTIONS = [
  'Through a job',
  'Bought it myself',
  'Tricare',
  'VA',
  'Medicare',
  'Medicaid',
] as const;
export type InsuranceSource = (typeof INSURANCE_SOURCE_OPTIONS)[number];

/** Sources the card treats as not eligible, whatever the BMI. */
export const NOT_ELIGIBLE_SOURCES: readonly InsuranceSource[] = ['Tricare', 'VA', 'Medicare', 'Medicaid'];

/** Bought it themselves: the source `out-if-self` network statuses turn on. */
export const SELF_BOUGHT_SOURCE: InsuranceSource = 'Bought it myself';

/** bariendo-be's payer key for Cigna, the one insurer with lower BMI bars. */
export const CIGNA_PAYER_KEY = 'cigna';

/** Mirrors bariendo-be's `InsuranceNetworkStatus`; null is unknown. */
export type NetworkStatus = 'in' | 'out' | 'out-if-self' | null;

interface Criteria {
  /** BMI at or above which no condition is needed. */
  alone: number;
  /** BMI at or above which one weight-related condition is enough. */
  withCondition: number;
}

const CIGNA_CRITERIA: Criteria = { alone: 35, withCondition: 30 };
const PRIVATE_CRITERIA: Criteria = { alone: 40, withCondition: 35 };

export interface InsuranceEligibilityInput {
  bmi: number | undefined;
  /** The selected weight-related conditions, in list order. */
  conditions: readonly string[];
  source: string | undefined;
  insurer: { name: string; key?: string } | undefined;
  network: NetworkStatus;
}

export interface InsuranceEligibility {
  eligible: boolean;
  label: string;
  /**
   * The BMI is in the condition band and no weight-related condition is
   * marked: eligible once one is (BAR-2998). The PN card v2.0 shows this as
   * conditionally eligible; the shipped card reads only `eligible` and `label`.
   */
  conditional?: true;
}

/** Absorbs float error from the division only, like bariendo-be's `BMI_TOLERANCE`. */
const BMI_TOLERANCE = 1e-9;

/**
 * Signup's comparison rule: the exact BMI against the bar, never a rounded one (BAR-2991). Both
 * sides compute that BMI from the same exact factors (`KG_PER_LB`, `CM_PER_INCH`; BAR-3018), so
 * they agree to float error for the same weight and height.
 */
const reaches = (bmi: number, bar: number): boolean => bmi + BMI_TOLERANCE >= bar;

/**
 * Truncated to one decimal, never rounded up, so the value shown reaches a bar exactly when
 * `reaches` says the BMI does: 39.95 reads 39.9, not a 40.0 it falls short of. The PN card shows
 * every BMI this way, so no number on it contradicts the line.
 */
export const formatBmi = (bmi: number): string => (Math.floor((bmi + BMI_TOLERANCE) * 10) / 10).toFixed(1);

/** What an eligible line adds when the plan's network couldn't be checked. */
const networkTailFor = (network: NetworkStatus): string => (network === null ? ' · Network unsure' : '');

/**
 * The likely-eligibility line, or undefined while there is not enough to say:
 * no source yet, or a private source with no insurer or no BMI.
 */
export function likelyInsuranceEligibility({
  bmi,
  conditions,
  source,
  insurer,
  network,
}: InsuranceEligibilityInput): InsuranceEligibility | undefined {
  if (!source) return undefined;
  if ((NOT_ELIGIBLE_SOURCES as readonly string[]).includes(source)) {
    return { eligible: false, label: `Not eligible yet · ${source}` };
  }
  if (!insurer || bmi === undefined || !Number.isFinite(bmi)) return undefined;

  const outOfNetwork = network === 'out' || (network === 'out-if-self' && source === SELF_BOUGHT_SOURCE);
  if (outOfNetwork) {
    return { eligible: false, label: `Not eligible yet · ${insurer.name} out of network` };
  }

  const criteria = insurer.key === CIGNA_PAYER_KEY ? CIGNA_CRITERIA : PRIVATE_CRITERIA;
  const bmiText = `BMI ${formatBmi(bmi)}`;
  const networkTail = networkTailFor(network);

  if (reaches(bmi, criteria.alone)) {
    return { eligible: true, label: `Meets criteria · ${bmiText}${networkTail}` };
  }
  if (reaches(bmi, criteria.withCondition)) {
    return conditions.length > 0
      ? { eligible: true, label: `Meets criteria · ${bmiText} + ${conditions[0]}${networkTail}` }
      : { eligible: false, conditional: true, label: `Needs a condition · ${bmiText}` };
  }
  return { eligible: false, label: `Not eligible yet · ${bmiText}` };
}

/**
 * The PN card v2.0's line (BAR-2998): the same verdict, except that a BMI in
 * the condition band with no condition marked reads as conditionally eligible
 * rather than "Needs a condition". Marking one turns it eligible.
 */
export function pnCardV2InsuranceEligibility(input: InsuranceEligibilityInput): InsuranceEligibility | undefined {
  const result = likelyInsuranceEligibility(input);
  if (!result?.conditional || input.bmi === undefined) return result;
  return {
    eligible: false,
    conditional: true,
    // The same network tail as the other eligible lines.
    label: `Eligible, conditional on a weight-related condition · BMI ${formatBmi(input.bmi)}${networkTailFor(input.network)}`,
  };
}
